import api from "crqlar-react-common/dist/services/api";
import {
    CrmChannelsTimeSeriesV4,
    CrmDemographicsChartsV4,
    CrmInsightsRequestV4,
    CrmOverviewTimeSeriesV4,
    CrmRatesTimeSeriesV4,
    CrmSeasonalityV4,
    CrmGuestValueV4,
    InsightRankedGuestExclusionUpdateV4,
    InsightRankedGuestExclusionV4,
    InsightRankedGuestListV4,
    InsightTileV4,
} from "../models/crm-v4";

// The route stays under /v3 because the API gateway rejects any other version prefix outright; the /v4 path
// segment is a temporary marker that comes off once the V3 CRM insights are retired.
export class CrmInsightsV4Service {
    private static baseUrl = "/v3/insights/crm/v4";

    // All thirteen overview stat cards. Every card always comes back — an unavailable one carries a `reason`
    // rather than being omitted, so the grid can render from a static list without layout shift.
    static async overview(request: CrmInsightsRequestV4): Promise<InsightTileV4[]> {
        const response = await api.post<InsightTileV4[]>(`${CrmInsightsV4Service.baseUrl}/overview`, request);

        return response.data;
    }

    static async overviewTimeSeries(request: CrmInsightsRequestV4): Promise<CrmOverviewTimeSeriesV4> {
        const response = await api.post<CrmOverviewTimeSeriesV4>(
            `${CrmInsightsV4Service.baseUrl}/overview/timeseries`,
            request,
        );

        return response.data;
    }

    static async overviewRatesTimeSeries(request: CrmInsightsRequestV4): Promise<CrmRatesTimeSeriesV4> {
        const response = await api.post<CrmRatesTimeSeriesV4>(
            `${CrmInsightsV4Service.baseUrl}/overview/rates/timeseries`,
            request,
        );
        return response.data;
    }

    // Booking channel mix over the same buckets as the rate series.
    static async overviewChannelsTimeSeries(request: CrmInsightsRequestV4): Promise<CrmChannelsTimeSeriesV4> {
        const response = await api.post<CrmChannelsTimeSeriesV4>(
            `${CrmInsightsV4Service.baseUrl}/overview/channels/timeseries`,
            request,
        );

        return response.data;
    }

    // Calendar-month room nights split into the fixed comparison years. Takes no page range, hence GET.
    static async overviewSeasonality(): Promise<CrmSeasonalityV4> {
        const response = await api.get<CrmSeasonalityV4>(`${CrmInsightsV4Service.baseUrl}/overview/seasonality`);

        return response.data;
    }

    // The arrivals cohort's demographic stat cards.
    static async demographics(request: CrmInsightsRequestV4): Promise<InsightTileV4[]> {
        const response = await api.post<InsightTileV4[]>(`${CrmInsightsV4Service.baseUrl}/demographics`, request);

        return response.data;
    }

    static async demographicCharts(request: CrmInsightsRequestV4): Promise<CrmDemographicsChartsV4> {
        const response = await api.post<CrmDemographicsChartsV4>(
            `${CrmInsightsV4Service.baseUrl}/demographics/charts`,
            request,
        );

        return response.data;
    }

    static async guestValue(request: CrmInsightsRequestV4): Promise<CrmGuestValueV4> {
        const response = await api.post<CrmGuestValueV4>(
            `${CrmInsightsV4Service.baseUrl}/demographics/guest-value`,
            request,
        );

        return response.data;
    }

    static async guestValueExclusions(): Promise<InsightRankedGuestExclusionV4[]> {
        const response = await api.get<InsightRankedGuestExclusionV4[]>(
            `${CrmInsightsV4Service.baseUrl}/demographics/guest-value/exclusions`,
        );
        return response.data;
    }

    static async excludeGuest(
        update: InsightRankedGuestExclusionUpdateV4,
    ): Promise<InsightRankedGuestExclusionV4[]> {
        const response = await api.put<InsightRankedGuestExclusionV4[]>(
            `${CrmInsightsV4Service.baseUrl}/demographics/guest-value/exclusions`,
            update,
        );
        return response.data;
    }

    static async includeGuest(
        ranking: InsightRankedGuestListV4,
        profileId: string,
    ): Promise<InsightRankedGuestExclusionV4[]> {
        const response = await api.delete<InsightRankedGuestExclusionV4[]>(
            `${CrmInsightsV4Service.baseUrl}/demographics/guest-value/exclusions/${ranking}/${encodeURIComponent(profileId)}`,
        );
        return response.data;
    }
}
